export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.gestionalo.app";
export const APP_STORE_URL =
  "https://apps.apple.com/uy/app/gestionalo/id6761935472";

export type AppStoreTarget = {
  href: string;
  platform: "android" | "ios";
} | null;

/**
 * Which store to offer the mobile app from, or none at all.
 *
 * IN ITS OWN `.ts` FILE, AND THAT IS THE POINT. It started inside
 * `AppDownloadBanner.tsx`, where it could not be tested: importing that
 * component pulls in the auth context and therefore `apiClient`, which reads
 * `import.meta.env` and explodes outside Vite. The house answer to that (see
 * `quote-deadline-chip.spec.ts`) is to restate the function inside the spec
 * and have the component point at it — which works, and admits in its own
 * comment that the copy can drift. A pure function with no React in its path
 * does not need either compromise: it moves here, and the component and the
 * spec import the same code.
 *
 * THE INTERESTING CASE IS THE ONE THAT RETURNS NULL. iOS Safari already shows
 * Apple's own Smart App Banner, from the `apple-itunes-app` meta tag in
 * index.html, so rendering ours underneath would stack two install prompts on
 * one screen. That is noise, not twice the nudge.
 *
 * Other iOS browsers — Chrome, Firefox, Edge — ignore that meta tag entirely,
 * so they still get ours. That is the whole reason this is a platform check
 * and not a simple `isAndroid`.
 *
 * DESKTOP GETS NOTHING. Someone on a laptop cannot install from the page in
 * front of them, and a banner you cannot act on is one people learn to
 * ignore — including the next time there is something worth saying.
 *
 * UA SNIFFING, KNOWINGLY. There is no feature detection for "which store
 * sells the app I am looking at", and both failure directions are mild: a
 * missed banner, or a link to a store the device does not have. Neither
 * blocks anything.
 */
export function resolveAppStoreTarget(userAgent: string): AppStoreTarget {
  const ua = userAgent.toLowerCase();

  if (/android/.test(ua)) return { href: PLAY_STORE_URL, platform: "android" };

  const isIos = /iphone|ipad|ipod/.test(ua);
  if (!isIos) return null;

  /*
    Every iOS browser reports "safari" in its user agent, Chrome and Edge
    included, so real Safari is the one reporting NONE of the others'
    markers — `crios` (Chrome), `fxios` (Firefox), `edgios` (Edge), `opt`
    (Opera). Checking for "safari" alone matches all of them and suppresses
    the banner on exactly the browsers that need it.
  */
  const isRealSafari = !/crios|fxios|edgios|opt\//.test(ua);
  if (isRealSafari) return null;

  return { href: APP_STORE_URL, platform: "ios" };
}
